import { FC, useEffect, useState } from 'react';

import { useConfirm } from 'material-ui-confirm';
import { Edit3, Lock, Trash2 } from 'react-feather';

import {
  IconButton,
  LinearProgress,
  Stack,
  TablePagination,
  Tooltip,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import { useGetPaginateUsers, useUserDelete } from '../../api-http/users';
import { DialogModal } from '../../components';
import { AlertBar } from '../../components/AlertBar';
import { notifyMsg } from '../../configs';
import { OrderEnum, SortEnum } from '../../types/pagination.types';
import { BaseUserWithOrg, UserRole } from '../../types/user.types';
import { displayDate } from '../../util/dateTime';
import { useIsUserRole } from '../../util/useIsUserRole';
import { useUserRoleName } from '../../util/useUserTypes';
import { UserUpdateForm } from './UserUpdateForm';
import { ResetPasswordForm } from '../admin/users/ResetPasswordForm';
import { useAuth } from '../../context/auth-context';
import { useTable } from '../../util/useTable';
import { useSnack } from '../../util/useSnack';
import {
  StyledTableCell,
  StyledTableSortLabel,
} from '../../components/TableTheme';
import { managementTableSx } from '../../components/ManagementPageLayout';

interface UsersTableProp {
  refresh: boolean;
  search: string;
}

const actionButtonSx = {
  width: 32,
  height: 32,
  border: '1px solid #dbe3ee',
  borderRadius: 2,
};

export const UsersTable: FC<UsersTableProp> = ({ refresh, search }) => {
  const confirm = useConfirm();
  const { switchedOrgId } = useAuth();
  const { showErrSnack, showSuccessSnack } = useSnack();
  const { sortOnClick, pagingParams, tableProps } = useTable();
  const { sortby, order } = pagingParams;

  const [userUpdateModal, setUserUpdateModal] = useState(false);
  const [activeUser, setActiveUser] = useState<BaseUserWithOrg>();
  const [userResetPasswordModal, setUserResetPasswordModal] = useState(false);

  const disableAccess = useIsUserRole([
    UserRole.user,
    UserRole.dataEditor,
    UserRole.layoutEditor,
  ]);

  const {
    isLoading: isLoadingUserDelete,
    error: errorUserDelete,
    mutate,
  } = useUserDelete({
    onSuccess: () => {
      showSuccessSnack(notifyMsg.USER_DELETE_SUCCESS);
      refetch();
    },
    onError: () => showErrSnack(notifyMsg.DELETE_ATTEMPT_FAILED),
  });

  const handleDelete = async (user: BaseUserWithOrg) => {
    try {
      await confirm({
        description: notifyMsg.DELETE_CONFIRMATION,
        confirmationText: 'Yes',
      });
      mutate({ id: user._id });
    } catch (error) {}
  };

  const onEditValue = (user: BaseUserWithOrg) => {
    setActiveUser(user);
    setUserUpdateModal(true);
  };

  const {
    data: list,
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useGetPaginateUsers({ ...pagingParams, search, organization: '' });

  useEffect(() => {
    refetch();
  }, [refresh, switchedOrgId, search]);

  return (
    <>
      {(isLoading || isLoadingUserDelete || isRefetching) && <LinearProgress />}
      <AlertBar severity="error" msg={error?.msg || errorUserDelete?.msg} />
      <TableContainer component={Paper} sx={managementTableSx}>
        <Table sx={{ minWidth: 760 }} aria-label="Users">
          <TableHead>
            <TableRow>
              <StyledTableCell>
                <StyledTableSortLabel
                  active={sortby === SortEnum.title || true}
                  direction={sortby === SortEnum.title ? order : OrderEnum.asc}
                  onClick={() => sortOnClick(SortEnum.title)}
                >
                  User Name
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="left">User Role</StyledTableCell>
              <StyledTableCell>Email</StyledTableCell>
              <StyledTableCell>
                <StyledTableSortLabel
                  active={sortby === SortEnum.updatedAt || true}
                  direction={
                    sortby === SortEnum.updatedAt ? order : OrderEnum.asc
                  }
                  onClick={() => sortOnClick(SortEnum.updatedAt)}
                >
                  Last updated
                </StyledTableSortLabel>
              </StyledTableCell>

              <StyledTableCell>
                <StyledTableSortLabel
                  active={sortby === SortEnum.createdAt || true}
                  direction={
                    sortby === SortEnum.createdAt ? order : OrderEnum.asc
                  }
                  onClick={() => sortOnClick(SortEnum.createdAt)}
                >
                  Created
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="center">Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list?.data.map((user) => {
              return (
                <TableRow key={user._id} sx={{ height: 56 }}>
                  <StyledTableCell component="th" scope="row">
                    {user.name}
                  </StyledTableCell>
                  <TableCell align="left">
                    {useUserRoleName(user.role)}
                  </TableCell>
                  <StyledTableCell>{user.email}</StyledTableCell>
                  <StyledTableCell>
                    {displayDate(user.updatedAt)}
                  </StyledTableCell>
                  <StyledTableCell>
                    {displayDate(user.createdAt)}
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <Stack
                      direction="row"
                      justifyContent="center"
                      spacing={0.75}
                    >
                      <Tooltip title="Edit user">
                        <span>
                          <IconButton
                            aria-label={`Edit ${user.name}`}
                            disabled={disableAccess}
                            onClick={() => onEditValue(user)}
                            sx={{ ...actionButtonSx, color: '#2563eb' }}
                          >
                            <Edit3 size={15} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {user.role !== UserRole.superAdmin && (
                        <Tooltip title="Reset password">
                          <span>
                            <IconButton
                              aria-label={`Reset password for ${user.name}`}
                              disabled={disableAccess}
                              onClick={() => {
                                setActiveUser(user);
                                setUserResetPasswordModal(true);
                              }}
                              sx={{ ...actionButtonSx, color: '#64748b' }}
                            >
                              <Lock size={15} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete user">
                        <span>
                          <IconButton
                            aria-label={`Delete ${user.name}`}
                            disabled={disableAccess}
                            onClick={() => handleDelete(user)}
                            sx={{
                              ...actionButtonSx,
                              color: '#ef4444',
                              borderColor: '#fecaca',
                              bgcolor: '#fffafa',
                            }}
                          >
                            <Trash2 size={15} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </StyledTableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          {...tableProps}
          component="div"
          hidden={list?.pagination.total === 0}
          count={list?.pagination.total || 0}
        />
      </TableContainer>
      <DialogModal
        title="Edit user"
        onClose={() => setUserUpdateModal(false)}
        open={userUpdateModal}
      >
        <UserUpdateForm
          user={activeUser}
          onFinished={() => {
            refetch();
            setUserUpdateModal(false);
          }}
        />
      </DialogModal>
      <DialogModal
        onClose={() => setUserResetPasswordModal(false)}
        open={userResetPasswordModal}
        title="Reset password"
      >
        <ResetPasswordForm
          onFinished={() => {
            refetch();
            setUserResetPasswordModal(false);
          }}
          userData={activeUser}
        />
      </DialogModal>
    </>
  );
};
